<script setup lang="ts">
/**
 * CallbackView — `/callback` route (Story 013.2 AC3, AC5, AC6).
 *
 * Parses the OAuth callback query from `location.search` (never the hash —
 * see `oauthCallback.ts`'s AC4 note), completes the exchange, persists the
 * resulting `TokenSet` to sessionStorage on success, and renders a masked
 * failure alert on error. A code-less, error-less hit (`reason:
 * 'missing_code'`) is a cold visit to `/callback`, not a real failure — it
 * navigates to `/login` quietly instead of showing an alert.
 */
import { onMounted, ref } from 'vue';
import type { ResolvedOAuth2Auth, TokenSet } from '@gl-demo-ultimate-odupre/connect-with-gitlab';
import { parseCallbackQuery, completeCallback } from '../services/oauthCallback';
import { persistTokenSet } from '../services/tokenPersistence';

const props = withDefaults(
  defineProps<{
    auth: ResolvedOAuth2Auth;
    location?: Pick<Location, 'search'>;
    sessionStorage?: Storage;
    navigate?: (path: string) => void;
    onSuccess?: (tokenSet: TokenSet) => void;
  }>(),
  {
    location: () => window.location,
    sessionStorage: () => window.sessionStorage,
    navigate: () => undefined,
    onSuccess: () => undefined,
  },
);

const errorMessage = ref<string | null>(null);

onMounted(async () => {
  const query = parseCallbackQuery(props.location);
  const result = await completeCallback(query, props.auth, { sessionStorage: props.sessionStorage });
  if (result.ok) {
    persistTokenSet(result.tokenSet, props.sessionStorage);
    props.onSuccess?.(result.tokenSet);
  } else if (result.reason === 'missing_code') {
    props.navigate('/login');
  } else {
    errorMessage.value = result.message;
  }
});
</script>

<template>
  <div class="cwc-callback-view">
    <p
      v-if="errorMessage"
      data-testid="callback-error"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>
